/**
 * SettingsHubIndex — Iter 189g Phase 1
 * Default view when hitting /website-builder/settings (no sub-page).
 * Shows a welcome + "jump to" cards for the most-used surfaces.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { SETTINGS_GROUPS } from './settingsGroups';

const QUICK_JUMP_IDS = ['general', 'domains', 'payments', 'shipping', 'integrations', 'billing'];

export default function SettingsHubIndex() {
  const flat = SETTINGS_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, _groupLabel: g.label })));
  const quickJump = QUICK_JUMP_IDS.map((id) => flat.find((x) => x.id === id)).filter(Boolean);

  return (
    <div className="max-w-3xl" data-testid="settings-hub-index">
      {/* Hero */}
      <div
        className="rounded-2xl p-8 mb-8 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(236,72,153,0.10) 100%)',
          border: '1px solid rgba(167,139,250,0.28)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-60" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.35) 0%, transparent 65%)' }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 mb-4">
            <Sparkles size={12} className="text-[#A78BFA]" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/85">Site Settings</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white font-syne">
            Everything about your store — in one place.
          </h2>
          <p className="text-sm text-white/70 mt-3 max-w-lg leading-relaxed">
            Configure your plan, domain, shipping rules, payment methods, notifications, and more.
            Use the left rail to jump between surfaces.
          </p>
        </div>
      </div>

      {/* Quick-jump cards */}
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A78BFA] mb-3">Jump to</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {quickJump.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.id}
              to={item.to}
              className="group flex items-center gap-4 p-4 rounded-xl transition-all hover:border-[#A78BFA]/40 hover:shadow-[0_18px_50px_-15px_rgba(139,92,246,0.35)]"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                border: '1px solid rgba(167,139,250,0.15)',
              }}
              data-testid={`settings-index-jump-${item.id}`}
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.25), rgba(236,72,153,0.15))', border: '1px solid rgba(167,139,250,0.35)' }}
              >
                <Icon size={16} className="text-[#A78BFA]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{item.label}</p>
                <p className="text-[11px] text-white/55 mt-0.5">{item._groupLabel}</p>
              </div>
              <ArrowRight size={14} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
            </Link>
          );
        })}
      </div>

      <p className="text-[11px] text-white/40 mt-8 leading-relaxed">
        &ldquo;Soon&rdquo; and &ldquo;Planned&rdquo; badges in the left rail mean the shell is live
        but the controls for that screen haven&rsquo;t been built yet. You can click through to
        see the placeholder — everything else is fully functional.
      </p>
    </div>
  );
}
